import { zodResolver } from '@hookform/resolvers/zod';
import { cisAssessmentSchema, type CisAssessment } from '@pgs/shared';
import { useForm } from 'react-hook-form';
import { focusFirstError } from '../focusFirstError';
import { NavButtons } from '../NavButtons';
import { StepPanel } from '../StepPanel';
import type { RegistrationDraft } from '../types';
import { DateInput } from '@/components/form/DateInput';
import { RadioGroup } from '@/components/form/RadioGroup';
import { TextField } from '@/components/form/TextField';

interface Props {
  defaultValues?: Partial<CisAssessment>;
  personal: RegistrationDraft['personal'];
  occupation: RegistrationDraft['occupation'];
  onBack: () => void;
  onSubmit: (values: CisAssessment) => void;
}

const todayDmy = (): string => {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const composeFullName = (p: RegistrationDraft['personal']): string =>
  [p.forename, p.middleName, p.surname]
    .map((s) => (s ?? '').trim())
    .filter(Boolean)
    .join(' ');

const YN = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const SELF_EMP_QS: Array<{ name: keyof CisAssessment; prompt: string }> = [
  {
    name: 'employmentBenefits',
    prompt:
      'a. Are you entitled to employment-style benefits such as holiday pay, sick pay, pension contributions, or similar?',
  },
  {
    name: 'submitsSelfAssessment',
    prompt:
      'b. Are you responsible for submitting your own Self Assessment tax return (personally or via an accountant)?',
  },
  {
    name: 'coversOwnExpenses',
    prompt:
      'c. Do you cover your own costs for travel, accommodation, and other work-related expenses?',
  },
  {
    name: 'canDeclineWork',
    prompt: 'd. Are you free to decline future work without obligation to accept further assignments?',
  },
  {
    name: 'multipleClients',
    prompt:
      'e. Can you work for multiple clients at the same time and determine your own working pattern (subject to site requirements)?',
  },
  {
    name: 'decidesHowWorkDone',
    prompt: 'f. Once tasks are outlined, do you decide how the work is carried out without detailed instruction?',
  },
  {
    name: 'subjectToSupervision',
    prompt: 'g. Are you subject to ongoing supervision while completing your work?',
  },
  {
    name: 'understandsSubstitution',
    prompt:
      'h. Do you understand that a suitably qualified substitute may be provided to complete the work if you are unavailable?',
  },
];

export const Step8CisAssessment = ({
  defaultValues,
  personal,
  occupation,
  onBack,
  onSubmit,
}: Props) => {
  const seededDefaults: Partial<CisAssessment> = {
    ...defaultValues,
    jobTitle: defaultValues?.jobTitle ?? occupation.role ?? '',
    fullName: defaultValues?.fullName ?? composeFullName(personal),
    contactNumber: defaultValues?.contactNumber ?? personal.mobile ?? '',
    date: defaultValues?.date ?? todayDmy(),
  };

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CisAssessment>({
    resolver: zodResolver(cisAssessmentSchema),
    defaultValues: seededDefaults,
    mode: 'onBlur',
  });

  const providesEq = watch('providesOwnEquipment');
  const receivesTr = watch('receivesTraining');

  return (
    <StepPanel
      icon="fa-hard-hat"
      title="CIS Self-Employed Status Assessment"
      description={
        <>
          <strong className="text-teal">People CIS Limited</strong>
          <br />
          <br />
          This questionnaire helps us assess your working arrangements and determine whether you
          operate as a genuinely self-employed contractor under the Construction Industry Scheme
          (CIS).
          <br />
          <br />
          Please answer all questions accurately based on your current or intended assignment.
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit, focusFirstError)}>
        <h3 className="text-[12px] font-bold uppercase tracking-wider text-navy">
          Contractor Details
        </h3>
        <TextField label="Job Title" {...register('jobTitle')} error={errors.jobTitle?.message} />
        <TextField
          label="Duration In Current Role"
          placeholder="e.g. 2 years"
          {...register('duration')}
          error={errors.duration?.message}
        />
        <label className="lbl">Relevant Qualifications / Certifications</label>
        <textarea className="inp" rows={2} {...register('qualifications')} />

        <h3 className="mt-6 text-[12px] font-bold uppercase tracking-wider text-navy">
          Role Description
        </h3>
        <label className="lbl">
          Please outline the nature of the work you will undertake, including responsibilities and
          how tasks are typically completed
        </label>
        <textarea
          className={`inp ${errors.roleDescription ? 'inp-error' : ''}`}
          rows={3}
          {...register('roleDescription')}
        />
        {errors.roleDescription && <p className="err-msg">{errors.roleDescription.message}</p>}

        <h3 className="mt-6 text-[12px] font-bold uppercase tracking-wider text-navy">
          Work History
        </h3>
        <TextField
          label="1. How many separate assignments have you completed in the past 12 months using your UTR number?"
          type="number"
          {...register('assignmentsInLast12Months')}
          error={errors.assignmentsInLast12Months?.message}
        />

        <h3 className="mt-6 text-[12px] font-bold uppercase tracking-wider text-navy">
          Self-Employment Indicators
        </h3>
        {SELF_EMP_QS.map((q) => (
          <RadioGroup
            key={q.name as string}
            control={control}
            name={q.name as never}
            label={q.prompt}
            options={YN}
          />
        ))}

        <h3 className="mt-6 text-[12px] font-bold uppercase tracking-wider text-navy">
          Equipment &amp; Materials
        </h3>
        <RadioGroup
          control={control}
          name="providesOwnEquipment"
          label="3. Do you provide your own tools, equipment, and/or personal protective equipment (PPE)?"
          options={YN}
        />
        {providesEq === 'no' && (
          <>
            <label className="lbl">If No, please provide details</label>
            <textarea className="inp" rows={2} {...register('equipmentDetails')} />
            {errors.equipmentDetails && (
              <p className="err-msg">{errors.equipmentDetails.message}</p>
            )}
          </>
        )}

        <h3 className="mt-6 text-[12px] font-bold uppercase tracking-wider text-navy">
          Training
        </h3>
        <RadioGroup
          control={control}
          name="receivesTraining"
          label="4. Apart from mandatory health and safety requirements, do you receive training from any party you provide services to?"
          options={YN}
        />
        {receivesTr === 'yes' && (
          <>
            <label className="lbl">If Yes, please provide details</label>
            <textarea className="inp" rows={2} {...register('trainingDetails')} />
            {errors.trainingDetails && <p className="err-msg">{errors.trainingDetails.message}</p>}
          </>
        )}

        <h3 className="mt-6 text-[12px] font-bold uppercase tracking-wider text-navy">
          Declaration
        </h3>
        <div className="cis-note">
          I confirm that the information provided is accurate and reflects the true nature of my
          working arrangements. I understand that this information will be used to assess my status
          for CIS purposes and determine the appropriate engagement model with People CIS Limited.
        </div>
        <label className="radio-opt mt-3.5">
          <input
            type="checkbox"
            {...register('declarationAccepted')}
            className="mt-0.5 h-[15px] w-[15px] accent-teal"
          />
          <span className="text-[11px] font-bold uppercase tracking-wider text-ink-700">
            I confirm and accept the above declaration
          </span>
        </label>
        {errors.declarationAccepted && <p className="err-msg">{errors.declarationAccepted.message}</p>}

        <TextField label="Full Name" {...register('fullName')} error={errors.fullName?.message} />
        <DateInput control={control} name="date" label="Date" />
        <TextField
          label="Contact Number"
          type="tel"
          {...register('contactNumber')}
          error={errors.contactNumber?.message}
        />

        <NavButtons onBack={onBack} submit />
      </form>
    </StepPanel>
  );
};
